import { COLOR_MASK, type Color, KING, PAWN, TYPE_MASK, WHITE, colorBit, other } from '../board.ts';
import { pieceDef, typeForLetter } from '../pieces.ts';
import { FenError } from '../notation.ts';

// A small second board stored in `ext` (the Pit, the duel board): `cols × rows` cells of piece
// codes, row 0 at White's side. Moves on it are actions with cell indexes in from/to.

export interface SideMove {
  from: number;
  to: number;
  capture: boolean;
  /** A pawn reached the far row. */
  promotes: boolean;
}

/** Pseudo-legal moves of `c`'s piece on cell `i` (standard chess geometry, no castling/double steps). */
export function sideMovesFrom(cells: ArrayLike<number>, cols: number, rows: number, i: number, c: Color, out: SideMove[]) {
  const p = cells[i];
  const t = p & TYPE_MASK;
  const them = colorBit(other(c));
  const f = i % cols;
  const r = Math.floor(i / cols);
  const on = (ff: number, rr: number) => ff >= 0 && ff < cols && rr >= 0 && rr < rows;
  const lastRow = c === WHITE ? rows - 1 : 0;
  if (t === PAWN) {
    const rr = r + (c === WHITE ? 1 : -1);
    if (!on(f, rr)) return;
    if (cells[rr * cols + f] === 0) out.push({ from: i, to: rr * cols + f, capture: false, promotes: rr === lastRow });
    for (const df of [-1, 1]) {
      if (on(f + df, rr) && cells[rr * cols + f + df] & them) out.push({ from: i, to: rr * cols + f + df, capture: true, promotes: rr === lastRow });
    }
    return;
  }
  const def = pieceDef(t);
  for (const [df, dr] of def.leaps ?? []) {
    if (!on(f + df, r + dr)) continue;
    const to = (r + dr) * cols + f + df;
    if (cells[to] === 0) out.push({ from: i, to, capture: false, promotes: false });
    else if (cells[to] & them) out.push({ from: i, to, capture: true, promotes: false });
  }
  for (const [df, dr] of def.slides ?? []) {
    let ff = f + df;
    let rr = r + dr;
    while (on(ff, rr)) {
      const to = rr * cols + ff;
      if (cells[to] === 0) out.push({ from: i, to, capture: false, promotes: false });
      else {
        if (cells[to] & them) out.push({ from: i, to, capture: true, promotes: false });
        break;
      }
      ff += df;
      rr += dr;
    }
  }
}

/** FEN-style rows, top row (the highest) first: "8/8/2N5/8". */
export function formatCells(cells: ArrayLike<number>, cols: number, rows: number): string {
  const out: string[] = [];
  for (let r = rows - 1; r >= 0; r--) {
    let row = '';
    let empty = 0;
    for (let f = 0; f < cols; f++) {
      const p = cells[r * cols + f];
      if (!p) {
        empty++;
        continue;
      }
      if (empty) row += empty;
      empty = 0;
      const l = pieceDef(p & TYPE_MASK).letter;
      row += p & colorBit(WHITE) ? l.toUpperCase() : l;
    }
    if (empty) row += empty;
    out.push(row);
  }
  return out.join('/');
}

export function parseCells(text: string, cols: number, rows: number, into: Int32Array, offset: number, allowed: readonly number[]) {
  const lines = text.split('/');
  if (lines.length !== rows) throw new FenError('Bad side board');
  lines.forEach((line, k) => {
    const r = rows - 1 - k;
    let f = 0;
    for (const ch of line) {
      if (ch >= '1' && ch <= '9') {
        f += Number(ch);
        continue;
      }
      const t = typeForLetter(ch);
      if (!allowed.includes(t) || f >= cols) throw new FenError('Bad side board piece');
      into[offset + r * cols + f] = t | colorBit(ch === ch.toUpperCase() ? WHITE : 1);
      f++;
    }
    if (f !== cols) throw new FenError('Bad side board row');
  });
}

/** Coloured, non-king piece (what can sit on a side board). */
export function sidePiece(p: number): boolean {
  return (p & COLOR_MASK) !== 0 && (p & TYPE_MASK) !== KING;
}

/** "a1"-style name of a side-board cell. */
export function cellName(i: number, cols: number): string {
  return 'abcdefgh'[i % cols] + String(Math.floor(i / cols) + 1);
}
